import { FC, useState } from "react";
import Message, { MessageRating } from "../../../models/conversation/Message";
import MessageBubbleActionBarItem from "./MessageBubbleActionBarItem";
import { useConversationHub } from "../../../hooks/useConversationHub";
import { ClipboardIcon, ThumbsDown, ThumbsUp } from "lucide-react";
import { showSuccessToast } from "@/components/custom-toasts/toasts";

type Props = {
  message: Message;
  isOverview?: boolean;
};

const MessageBubbleActionBar: FC<Props> = ({ message, isOverview = false }) => {
  const { rateMessage } = useConversationHub();

  const [messageWasCopied, setMessageWasCopied] = useState<boolean>(false);

  const handleRateMessage = (rating: MessageRating) => {
    rateMessage(message, rating);
  };

  const handleCopyToClipboard = () => {
    const escapedMessageId =
      typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(message.id)
        : message.id.replace(/"/g, '\\"');

    const copySource = document.querySelector(
      `[data-markdown-copy-source="${escapedMessageId}"]`
    );

    if (!copySource) {
      navigator.clipboard.writeText(message.text);
      setMessageWasCopied(true);
      showSuccessToast("Beskeden blev kopieret til udklipsholderen");
      return;
    }

    const selection = window.getSelection();
    const previousRanges: Range[] = [];

    if (selection) {
      for (let i = 0; i < selection.rangeCount; i++) {
        previousRanges.push(selection.getRangeAt(i).cloneRange());
      }
    }

    const range = document.createRange();
    range.selectNodeContents(copySource);

    selection?.removeAllRanges();
    selection?.addRange(range);

    const copied = document.execCommand("copy");

    selection?.removeAllRanges();
    previousRanges.forEach((previousRange) => selection?.addRange(previousRange));

    if (!copied) {
      navigator.clipboard.writeText(message.text);
    }

    setMessageWasCopied(true);
    showSuccessToast("Beskeden blev kopieret til udklipsholderen");
  };
  const noRating = message.rating === MessageRating.NeutralVote;
  return (
    <>
      {isOverview && noRating ? (
        <></>
      ) : (
        <div className="absolute flex -bottom-11 left-0 py-2 px-1">
          <MessageBubbleActionBarItem
            icon={ThumbsUp}
            active={message.rating === MessageRating.UpVote}
            activeClassName="text-success"
            className={isOverview ? "cursor-default hover:bg-transparent" : ""}
            onClick={() => {
              if (!isOverview) {
                handleRateMessage(MessageRating.UpVote);
              }
            }}
            testId="up-vote"
          />
          <MessageBubbleActionBarItem
            icon={ThumbsDown}
            active={message.rating === MessageRating.DownVote}
            activeClassName="text-destructive"
            className={isOverview ? "cursor-default hover:bg-transparent" : ""}
            onClick={() => {
              if (!isOverview) {
                handleRateMessage(MessageRating.DownVote);
              }
            }}
            testId="down-vote"
          />

          {!isOverview && (
            <MessageBubbleActionBarItem
              icon={ClipboardIcon}
              active={messageWasCopied}
              activeClassName="text-primary"
              onClick={handleCopyToClipboard}
              testId="copy-to-clipboard"
            />
          )}
        </div>
      )}
    </>
  );
};

export default MessageBubbleActionBar;
